<script setup lang="ts">
import VueMultiselect from 'vue-multiselect';
import {
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
import { useStore } from 'vuex';

import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '../../../../../assets/styles/qill.scss';
import GlowPicker from '@/components/StickerTIcket/GlowPicker.vue';

const props = defineProps<{
  availableTags: string[];
}>();

const store = useStore();
const allowNamingPrices = ref(false);

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});

// Function to sanitize slug input - only allow lowercase letters, numbers, and hyphens
const sanitizeSlug = () => {
  item.value.slug = item.value.slug.replace(/[^a-z0-9-]/g, '').toLowerCase();
};

// Computed property for slug tooltip
const slugTip = computed(() => {
  return 'A slug is a URL-friendly version of your sticker name. It creates clean, memorable URLs like "spotlight.xyz/web/sticker/your-sticker-name". '
    + 'Slugs improve SEO, make sharing easier, and provide a professional appearance. Use lowercase letters, numbers, and hyphens only.';
});

onMounted(() => {
  if (item?.value?.quickCosts?.length) {
    if (item.value.quickCosts.length !== 4) {
      const newArray = Array(4).fill(null);

      for (let i = 0; i < Math.min(item.value.quickCosts.length, 4); i += 1) {
        newArray[i] = item.value.quickCosts[i];
      }
      item.value.quickCosts = newArray;
    }

    const hasValidQuickCost = item.value.quickCosts.some((cost) =>
      cost !== null && cost !== undefined && cost !== '' && cost !== 0);
    allowNamingPrices.value = hasValidQuickCost;

    if (!hasValidQuickCost && item.value.quickCosts) {
      for (let i = 0; i < item.value.quickCosts.length; i += 1) {
        item.value.quickCosts[i] = null;
      }
    }
  } else {
    if (!item.value.quickCosts) {
      item.value.quickCosts = [null, null, null, null];
    }
    allowNamingPrices.value = false;
  }
});

watch(() => [...(item.value.quickCosts || [])], (newQuickCosts) => {
  const hasQuickCosts = newQuickCosts.some((cost) => cost !== null && cost !== undefined && cost !== '' && cost !== 0);

  if (hasQuickCosts && allowNamingPrices.value) {
    item.value.cost = '0';
  }
}, { deep: true });

watch(allowNamingPrices, (newValue) => {
  if (!item.value.quickCosts) {
    item.value.quickCosts = newValue ? [0, 0, 0, 0] : [null, null, null, null];
  }

  if (newValue) {
    item.value.cost = '0';
  } else {
    for (let i = 0; i < item.value.quickCosts.length; i += 1) {
      item.value.quickCosts[i] = null;
    }
  }
});

const prepareForSave = () => {
  if (!item.value.quickCosts) {
    item.value.quickCosts = allowNamingPrices.value ? [0, 0, 0, 0] : [null, null, null, null];
  }

  if (!Array.isArray(item.value.quickCosts) || item.value.quickCosts.length !== 4) {
    const newArray = Array(4).fill(null);
    if (Array.isArray(item.value.quickCosts)) {
      for (let i = 0; i < Math.min(item.value.quickCosts.length, 4); i += 1) {
        newArray[i] = item.value.quickCosts[i];
      }
    }
    item.value.quickCosts = newArray;
  }

  const hasQuickCosts = item.value.quickCosts.some((cost) =>
    cost !== null && cost !== undefined && cost !== '' && cost !== 0);

  if (allowNamingPrices.value && hasQuickCosts) {
    item.value.cost = '0';

    for (let i = 0; i < item.value.quickCosts.length; i += 1) {
      if (item.value.quickCosts[i] !== null && item.value.quickCosts[i] !== undefined
          && item.value.quickCosts[i] !== '') {
        item.value.quickCosts[i] = Number(item.value.quickCosts[i]);
        if (Number.isNaN(item.value.quickCosts[i]) || item.value.quickCosts[i] === 0) {
          item.value.quickCosts[i] = null;
        }
      } else {
        item.value.quickCosts[i] = null;
      }
    }
  } else if (!allowNamingPrices.value) {
    if (!item.value.cost || item.value.cost === '') {
      item.value.cost = '0';
    }

    for (let i = 0; i < item.value.quickCosts.length; i += 1) {
      item.value.quickCosts[i] = null;
    }
  }
};

defineExpose({ prepareForSave });

const preventArrowKeys = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
  }
};
</script>

<template>
  <section class="c-default-sticker">
    <article class="c-default-sticker__row c-default-sticker__row--column">
      <span class="c-default-sticker__label">Sticker Title*</span>
      <input
        id="sticker-title"
        class="c-input"
        type="text"
        required
        placeholder="Title"
        v-model="item.title"
      />
    </article>

    <article class="c-default-sticker__row c-default-sticker__row--column">
      <span class="c-default-sticker__label">
        Slug
        <el-tooltip
          effect="customized"
          :content="slugTip"
          placement="top"
          popper-class="max-w-350"
        >
          <fai type="fal" icon="info-circle" class="size-18 ms-2" />
        </el-tooltip>
      </span>
      <input
        id="sticker-slug"
        class="c-input"
        type="text"
        placeholder="your-sticker-name"
        v-model="item.slug"
        @input="sanitizeSlug"
      />
    </article>

    <article class="c-default-sticker__row c-default-sticker__row--tags-cost">
      <div class="c-default-sticker__row c-default-sticker__row--column">
        <span class="c-default-sticker__label">Tags*</span>
        <VueMultiselect
          class="c-input-multiselect"
          v-model="item.tags"
          :multiple="true"
          :options="availableTags"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          placeholder="Tag"
        />
      </div>
      <div class="c-default-sticker__row c-default-sticker__row--column">
        <span class="c-default-sticker__label">Cost</span>
        <input
          id="sticker-cost"
          v-model="item.cost"
          class="c-input"
          type="text"
          required
          placeholder="Cost"
          :disabled="allowNamingPrices"
        />
      </div>
    </article>

    <article class="c-quick-costs">
      <el-checkbox v-model="allowNamingPrices">Allow user's to name price</el-checkbox>

      <div v-if="allowNamingPrices" class="c-quick-costs__container">
        <p class="c-quick-costs__title">Quick cost options</p>
        <div class="c-quick-costs__costs">
          <section class="position-relative" v-for="(cost, index) in 4" :key="index">
            <div class="c-input-prefix" v-show="item.quickCosts[index]">$</div>
            <input
              :id="`quick-cost-${index}`"
              class="c-input"
              v-model.number="item.quickCosts[index]"
              size="10"
              :placeholder="`Cost ${cost}`"
              type="number"
              step="1"
              min="0"
              @keydown="preventArrowKeys"
              @wheel.prevent
            />
          </section>
        </div>
      </div>
    </article>

    <article class="c-default-sticker__row c-default-sticker__row--column">
      <span class="c-default-sticker__label">Details*</span>
      <QuillEditor
        :toolbar="['bold', 'italic', 'underline', 'strike']"
        placeholder=""
        v-model:content="item.description"
        content-type="html"
      />
    </article>

    <!-- <glow-picker /> -->
  </section>
</template>

<style scoped lang="scss">
.c-default-sticker {
  display: flex;
  flex-direction: column;
  gap: 30px;
  min-width: 340px;

  &__label {
    margin-bottom: 5px;
    font-size: 14px;
    font-weight: 500;
  }

  &__row {
    display: flex;

    .multiselect {
      max-width: 160px;

      &__select {
        display: none;
      }
    }
    &--column {
      flex-direction: column;
    }

    &--tags-cost {
      gap: 20px;
      >div {
        width: calc(50% - 10px);
      }
    }
  }

  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
}
</style>
